import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Paper,
    Typography,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Grid } from "@material-ui/core";
import { DataGrid, GridColDef } from "@material-ui/data-grid";
import { makeGETRequestOnUserService } from "../../Api/api";
import moment from "moment";

interface PaymentDetailsProps {
    userUUID: string;
}

interface Payment {
    uuid: string;
    payment_id: number;
    amc_order_id: number | null;
    amount: string;
    debit_confirmed_at: string | null;
    failed_at: string | null;
    failed_reason: string | null;
    failure_code: string | null;
    mandate_id: number | null;
    method: string;
    payment_reference: string | null;
    payment_type: string;
    provider_name: string;
    status: string;
    submitted_at: string | null;
    inserted_at: string | null;
    updated_at: string | null;
}

interface Plan {
    uuid?: string;
    scheme?: string;
    scheme_name?: string;
    purchase_plan_id?: string;
    purchase_id?: string;
    state?: string;
    folio_number?: string | null;
    frequency?: string;
    amount?: number;
    payments?: Payment[];
    created_at?: string | null;
}

interface PlanRow {
    id: string;
    sr_no: number;
    scheme: string;
    scheme_name: string;
    folio_number: string;
    frequency: string;
    plan_amount: number;
    state: string;
    success_count: number;
    failed_count: number;
    last_activity: string | null;
    payments: Payment[];
    created_at?: string | null;
}

const ALLOWED_STATUSES = ["SUCCESS", "FAILED"];

const PaymentDetails = ({ userUUID }: PaymentDetailsProps) => {
    const toastr = require("toastr");
    const [loading, setLoading] = useState(false);
    const [paymentType, setPaymentType] = useState<"lumpsum" | "sip">("lumpsum");
    const [sipRows, setSipRows] = useState<PlanRow[]>([]);
    const [lumpsumRows, setLumpsumRows] = useState<PlanRow[]>([]);
    const [selectedPlan, setSelectedPlan] = useState<PlanRow | null>(null);

    const formatDate = (date?: string | null) => {
        if (!date) return "-";
        const parsed = moment(date);
        return parsed.isValid() ? parsed.format("DD MMM YYYY HH:mm") : date;
    };

    const getStatusStyle = (status: string) => {
        const colors: Record<string, string> = {
            SUCCESS: "#2e7d32",
            FAILED: "#d32f2f",
        };
        return { color: colors[status] || "#323232", fontWeight: 600 };
    };

    const preparePlanRows = (
        plans: Plan[],
        type: "sip" | "lumpsum"
    ): PlanRow[] => {
        if (!Array.isArray(plans)) return [];

        const rows = plans
            .filter((plan) =>
                type === "sip"
                    ? !!plan.purchase_plan_id
                    : !!plan.purchase_id
            )
            .map((plan) => {
                const filtered = (plan.payments || [])
                    .filter((p) => ALLOWED_STATUSES.includes(p.status))
                    .sort(
                        (a, b) =>
                            new Date(b.inserted_at || 0).getTime() -
                            new Date(a.inserted_at || 0).getTime()
                    );

                return {
                    id: (type === "sip"
                        ? plan.purchase_plan_id
                        : plan.purchase_id) as string,

                    sr_no: 0,

                    scheme: plan.scheme || "-",
                    scheme_name: plan.scheme_name || "-",
                    folio_number: plan.folio_number || "-",
                    frequency: plan.frequency || "-",
                    plan_amount: plan.amount || 0,
                    state: plan.state || "-",
                    created_at: plan.created_at || null,

                    success_count: filtered.filter(
                        (p) => p.status === "SUCCESS"
                    ).length,

                    failed_count: filtered.filter(
                        (p) => p.status === "FAILED"
                    ).length,

                    last_activity: filtered.length
                        ? filtered[0].inserted_at
                        : null,

                    payments: filtered,
                };
            })
            .sort(
                (a, b) =>
                    new Date(b.created_at || 0).getTime() -
                    new Date(a.created_at || 0).getTime()
            );

        return rows.map((row, index) => ({
            ...row,
            sr_no: index + 1,
        }));
    };

    const fetchPayments = async () => {
        if (!userUUID) {
            toastr.error("User UUID not found.");
            setSipRows([]);
            setLumpsumRows([]);
            return;
        }

        try {
            setLoading(true);
            const response = await makeGETRequestOnUserService(
                `/mutual_fund/mf_order_payments_list/${userUUID}/`
            );

            if (response?.status === 200) {
                const data = response?.data?.data;
                const sip = preparePlanRows(data?.sip || [], "sip");
                const lumpsum = preparePlanRows(data?.lumpsum || [], "lumpsum");

                setSipRows(sip);
                setLumpsumRows(lumpsum);

                if (!sip.length && !lumpsum.length) {
                    toastr.info("No payments found.");
                }
            } else {
                toastr.error(response?.data?.message || "Failed to fetch Payments.");
                setSipRows([]);
                setLumpsumRows([]);
            }
        } catch (error: any) {
            console.error("Failed to fetch Payments:", error);
            setSipRows([]);
            setLumpsumRows([]);
            toastr.error(
                error?.response?.data?.message ||
                error?.message ||
                "Something went wrong while fetching Payments."
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPayments();
    }, [userUUID]);

    const handlePaymentTypeChange = (
        _e: React.MouseEvent<HTMLElement>,
        newType: "lumpsum" | "sip" | null
    ) => {
        if (newType !== null) setPaymentType(newType);
    };

    const idHeader = paymentType === "sip" ? "Purchase Plan ID" : "Purchase ID";

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: "sr_no",
                headerName: "Sr No.",
                width: 120,
            },
            {
                field: "scheme",
                headerName: "Scheme",
                width: 180,
            },
            {
                field: "scheme_name",
                headerName: "Scheme Name",
                width: 280,
                renderCell: (params: any) => {
                    const value = params.value || "-";

                    return (
                        <Tooltip title={value} placement="top" arrow>
                            <span
                                style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    display: "block",
                                    width: "100%",
                                    cursor: "pointer",
                                }}
                            >
                                {value.charAt(0).toUpperCase() + value.slice(1)}
                            </span>
                        </Tooltip>
                    );
                },
            },
            { field: "folio_number", headerName: "Folio Number", width: 170 },
            ...(paymentType === "sip"
                ? [
                    {
                        field: "frequency",
                        headerName: "Frequency",
                        width: 150,
                        renderCell: (params: any) => {
                            const value = params.value || "-";
                            return value.charAt(0).toUpperCase() + value.slice(1);
                        },
                    },
                ]
                : []),
            {
                field: "plan_amount",
                headerName: "Amount",
                width: 130,
                renderCell: (params: any) => `₹${Number(params.value || 0).toFixed(2)}`,
            },
            {
                field: "state",
                headerName: "State",
                width: 120,
                renderCell: (params: any) => {
                    const value = params.value || "-";
                    return value.charAt(0).toUpperCase() + value.slice(1);
                },
            },
            {
                field: "created_at",
                headerName: "Created At",
                width: 160,
                renderCell: (params: any) => formatDate(params.value),
            },
            { field: "success_count", headerName: "Success", width: 140 },
            { field: "failed_count", headerName: "Failed", width: 120 },
            {
                field: "last_activity",
                headerName: "Last Payment",
                width: 190,
                renderCell: (params: any) => formatDate(params.value),
            },
        ],
        [paymentType]
    );

    if (loading) {
        return (
            <Box
                sx={{
                    height: 300,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 2,
                }}
            >
                <CircularProgress sx={{ color: "#262349" }} />
                <Typography color="text.secondary">Loading Payments...</Typography>
            </Box>
        );
    }

    if (!sipRows.length && !lumpsumRows.length) {
        return (
            <Box
                sx={{
                    height: 300,
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Typography color="text.secondary" sx={{ textAlign: "center", fontSize: "16px" }}>
                    No Payments found.
                </Typography>
            </Box>
        );
    }

    const rows = paymentType === "sip" ? sipRows : lumpsumRows;

    return (
        <Grid className="QuotesDataGrid">
            <Grid style={{ padding: "0 50px" }}>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            backgroundColor: "#f0f0f5",
                            borderRadius: "12px",
                            padding: "4px",
                            position: "relative",
                        }}
                    >
                        <Box
                            sx={{
                                position: "absolute",
                                top: 4,
                                left: paymentType === "lumpsum" ? 4 : "calc(50% + 2px)",
                                width: "calc(50% - 6px)",
                                height: "calc(100% - 8px)",
                                backgroundColor: "#262349",
                                borderRadius: "10px",
                                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                boxShadow: "0 2px 8px rgba(38, 35, 73, 0.3)",
                                zIndex: 0,
                            }}
                        />

                        <Box
                            onClick={() => setPaymentType("lumpsum")}
                            sx={{
                                px: 3,
                                py: 1,
                                borderRadius: "10px",
                                cursor: "pointer",
                                position: "relative",
                                zIndex: 1,
                                transition: "color 0.3s ease",
                                color: paymentType === "lumpsum" ? "#fff" : "#6b6b80",
                                fontWeight: paymentType === "lumpsum" ? 600 : 500,
                                fontSize: "0.8rem",
                                letterSpacing: "0.02em",
                                userSelect: "none",
                                display: "flex",
                                alignItems: "center",
                                gap: 0.8,
                                "&:hover": {
                                    color: paymentType === "lumpsum" ? "#fff" : "#262349",
                                },
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    backgroundColor: paymentType === "lumpsum" ? "#4caf50" : "#bdbdbd",
                                    transition: "all 0.3s ease",
                                    boxShadow: paymentType === "lumpsum"
                                        ? "0 0 6px rgba(76, 175, 80, 0.5)"
                                        : "none",
                                }}
                            />
                            Lumpsum
                            {lumpsumRows.length > 0 && (
                                <Box
                                    component="span"
                                    sx={{
                                        marginRight: 2,
                                        fontSize: "0.65rem",
                                        backgroundColor:
                                            paymentType === "lumpsum"
                                                ? "rgba(255,255,255,0.2)"
                                                : "#e0e0ea",
                                        color: paymentType === "lumpsum" ? "#fff" : "#6b6b80",
                                        px: 0.8,
                                        py: 0.2,
                                        borderRadius: "6px",
                                        fontWeight: 600,
                                        lineHeight: 1,
                                        transition: "all 0.3s ease",
                                    }}
                                >
                                    {lumpsumRows.length}
                                </Box>
                            )}
                        </Box>

                        <Box
                            onClick={() => setPaymentType("sip")}
                            sx={{
                                px: 3,
                                py: 1,
                                borderRadius: "10px",
                                cursor: "pointer",
                                position: "relative",
                                zIndex: 1,
                                transition: "color 0.3s ease",
                                color: paymentType === "sip" ? "#fff" : "#6b6b80",
                                fontWeight: paymentType === "sip" ? 600 : 500,
                                fontSize: "0.8rem",
                                letterSpacing: "0.02em",
                                userSelect: "none",
                                display: "flex",
                                alignItems: "center",
                                gap: 0.8,
                                "&:hover": {
                                    color: paymentType === "sip" ? "#fff" : "#262349",
                                },
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    backgroundColor: paymentType === "sip" ? "#4caf50" : "#bdbdbd",
                                    transition: "all 0.3s ease",
                                    boxShadow: paymentType === "sip"
                                        ? "0 0 6px rgba(76, 175, 80, 0.5)"
                                        : "none",
                                }}
                            />
                            SIP
                            {sipRows.length > 0 && (
                                <Box
                                    component="span"
                                    sx={{
                                        fontSize: "0.65rem",
                                        backgroundColor:
                                            paymentType === "sip"
                                                ? "rgba(255,255,255,0.2)"
                                                : "#e0e0ea",
                                        color: paymentType === "sip" ? "#fff" : "#6b6b80",
                                        px: 0.8,
                                        py: 0.2,
                                        borderRadius: "6px",
                                        fontWeight: 600,
                                        lineHeight: 1,
                                        transition: "all 0.3s ease",
                                    }}
                                >
                                    {sipRows.length}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>

                <Paper elevation={1}>
                    <Box p={2}>
                        <Typography variant="subtitle1" fontWeight={600} mb={2}>
                            {paymentType === "sip" ? "SIP Payments" : "Lumpsum Payments"}
                        </Typography>

                        <DataGrid
                            rows={rows}
                            columns={columns}
                            autoHeight
                            disableSelectionOnClick
                            pageSize={10}
                            rowsPerPageOptions={[10, 25, 50]}
                            getRowId={(row) => row.id}
                            onRowClick={(params: any) => setSelectedPlan(params.row)}
                        />
                    </Box>
                </Paper>
            </Grid>

            <Dialog
                open={!!selectedPlan}
                onClose={() => setSelectedPlan(null)}
                maxWidth="xl"
                fullWidth
            >
                <DialogTitle sx={{ pr: 6 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="h6" fontWeight={600}>
                                Payments (Success / Failed)
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {selectedPlan?.scheme} — {selectedPlan?.scheme_name}
                            </Typography>
                        </Box>

                        {selectedPlan && (
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.8,
                                        backgroundColor: "#e8f5e9",
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: "8px",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: "50%",
                                            backgroundColor: "#2e7d32",
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            color: "#2e7d32",
                                            fontSize: "0.8rem",
                                        }}
                                    >
                                        Success: {selectedPlan.success_count}
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.8,
                                        backgroundColor: "#fdecea",
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: "8px",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: "50%",
                                            backgroundColor: "#d32f2f",
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            color: "#d32f2f",
                                            fontSize: "0.8rem",
                                        }}
                                    >
                                        Failed: {selectedPlan.failed_count}
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.8,
                                        backgroundColor: "#f0f0f5",
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: "8px",
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            color: "#262349",
                                            fontSize: "0.8rem",
                                        }}
                                    >
                                        Total: {selectedPlan.payments.length}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Box>

                    <IconButton
                        onClick={() => setSelectedPlan(null)}
                        sx={{ position: "absolute", right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    {selectedPlan && selectedPlan.payments.length === 0 ? (
                        <Typography color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
                            No SUCCESS or FAILED payments found for this plan.
                        </Typography>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Payment ID</TableCell>
                                        <TableCell>AMC Order ID</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Method</TableCell>
                                        <TableCell>Amount</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Payment Reference</TableCell>
                                        <TableCell>Mandate ID</TableCell>
                                        <TableCell>Submitted At</TableCell>
                                        <TableCell>Debit Confirmed</TableCell>
                                        <TableCell>Failed At</TableCell>
                                        <TableCell>Failure Reason</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {selectedPlan?.payments.map((p) => (
                                        <TableRow key={p.uuid}>
                                            <TableCell>{p.payment_id}</TableCell>
                                            <TableCell>{p.amc_order_id ?? "-"}</TableCell>
                                            <TableCell>{p.payment_type}</TableCell>
                                            <TableCell>{p.method}</TableCell>
                                            <TableCell>₹{Number(p.amount || 0).toFixed(2)}</TableCell>
                                            <TableCell>
                                                <span style={getStatusStyle(p.status)}>{p.status}</span>
                                            </TableCell>
                                            <TableCell>{p.payment_reference || "-"}</TableCell>
                                            <TableCell>{p.mandate_id ?? "-"}</TableCell>
                                            <TableCell>{formatDate(p.submitted_at)}</TableCell>
                                            <TableCell>{formatDate(p.debit_confirmed_at)}</TableCell>
                                            <TableCell>{formatDate(p.failed_at)}</TableCell>
                                            <TableCell>{p.failed_reason || "-"}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
            </Dialog>
        </Grid>
    );
};

export default PaymentDetails;